// Thin client over the refauth REST API and the user's CouchDB.
//
//   POST /auth/login                       -> { token, password, userDBs.supertest, ... }
//   POST /live/:matchId/event              -> 200 OK  (auth: Bearer token:password)
//   PUT  {userDBs.supertest}/:matchId      -> 201/200 { ok, id, rev }
//                                             (auth: Basic base64(token:password))
//
// Bearer auth is documented in refauth/features/auth/SuperloginDummyController.js.
// Direct CouchDB writes are how the phone app persists matches (it uses PouchDB
// against the same `userDBs.supertest` URL — see phone/src/services/matchService.ts).

export interface AuthToken {
  token: string;
  password: string;
  user_id: string;
  expires: number;
  userDBs?: { supertest?: string };
}

export interface ApiResponse<T = unknown> {
  ok: boolean;
  status: number;
  body: T | string;
}

export interface CouchPutResult {
  ok: boolean;
  id: string;
  rev: string;
}

function bearer(auth: AuthToken): string {
  return `Bearer ${auth.token}:${auth.password}`;
}

function basic(auth: AuthToken): string {
  return `Basic ${btoa(`${auth.token}:${auth.password}`)}`;
}

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

async function parseBody(res: Response): Promise<unknown | string> {
  const text = await res.text();
  if (!text) return "";
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function login(
  baseUrl: string,
  username: string,
  password: string
): Promise<ApiResponse<AuthToken>> {
  const res = await fetch(joinUrl(baseUrl, "/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const body = (await parseBody(res)) as AuthToken | string;
  return { ok: res.ok, status: res.status, body };
}

export async function postLiveEvent(
  baseUrl: string,
  auth: AuthToken,
  matchId: string,
  event: Record<string, unknown>
): Promise<ApiResponse<string>> {
  const res = await fetch(joinUrl(baseUrl, `/live/${matchId}/event`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: bearer(auth),
    },
    body: JSON.stringify(event),
  });
  const body = (await parseBody(res)) as string;
  return { ok: res.ok, status: res.status, body };
}

// PUT a brand-new match document into the user's CouchDB.
// `userDbUrl` is the value of `session.userDBs.supertest` returned by /auth/login.
// It typically looks like `http://TOKEN:PASSWORD@host:5984/supertest$USERNAME` —
// we strip the embedded credentials and send an explicit `Authorization: Basic`
// header so the call works under modern browser fetch rules.
export async function putMatchDoc(
  userDbUrl: string,
  auth: AuthToken,
  matchId: string,
  doc: Record<string, unknown>
): Promise<ApiResponse<CouchPutResult>> {
  const cleanDbUrl = stripUrlCredentials(userDbUrl);
  const url = `${cleanDbUrl.replace(/\/$/, "")}/${encodeURIComponent(matchId)}`;
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: basic(auth),
    },
    body: JSON.stringify(doc),
  });
  const body = (await parseBody(res)) as CouchPutResult | string;
  return { ok: res.ok, status: res.status, body };
}

// Removes any `user:pass@` prefix from a URL. CouchDB user-DB URLs returned by
// the login response embed credentials inline; sending those to fetch() is
// deprecated, so we send them as a Basic auth header instead.
export function stripUrlCredentials(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    u.username = "";
    u.password = "";
    return u.toString();
  } catch {
    // Best-effort fallback for inputs that aren't whatwg-parseable.
    return rawUrl.replace(/^([a-z]+:\/\/)[^@/]+@/i, "$1");
  }
}
